import { useState, memo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { BodyBig, BodySmall, BodyXS, Box, CloseIcon, FileSelector, H6, Grid, IconButton, InfoIcon, Tooltip, Skeleton, Button, Snackbar } from '@parspec/pixel';

import { useLocationDetails } from '../context';

import { MultipleBranchesModal } from '../../shared/components/MultipleBranchesModal/MultipleBranchesModal';
import { SaveButton } from '../../shared/components/SaveButton';
import { useEditBranchBrandingMutation } from '../../queries';
import { useParspecNavigation } from 'src/app/Context';
import { EDIT_SETTINGS_SUCCESS_MSG } from '../../shared/constants';
import { IFileType } from '../QuoteTemplatePreferences/utils/helper';
import { environment } from 'src/environments';
import { getProxyFileUrl } from 'src/features/shared/utils/pdfUtils';

const ACCEPTED_FILE_TYPES = ['.png', '.jpg'];

const PRIMARY_LOGO_TOOLTIP_TEXT = 'The primary logo is used as the default image on the cover page and table of contents. Wide logos and wordmarks materialize best as primary logo.';

const SECONDARY_LOGO_TOOLTIP_TEXT =
    'The secondary logo is used as the default image for the ‘prepared by’ section on the cover page, and for the datasheet headers. Square logos materialize best as secondary logo';

const SAMPLE_SUBMITTAL_PREVIEW_FIRST_PARA = 'Logos and colors are used to create documents in no time and maintain consistency across projects of the same branch.';

const SAMPLE_SUBMITTAL_PREVIEW_SECOND_PARA =
    'Below is an example of how your brand will be applied to newly created documents by default. You can always customize the layout during the document creation process.';

const LOGO_TYPES: { label: string; value: 'logo' | 'picture' }[] = [
    { label: 'Primary Logo', value: 'logo' },
    { label: 'Secondary Logo', value: 'picture' }
];

const BranchBranding = () => {
    const { currentLocationId, companyDetailsLoading, branchBrandingTemplate, setBranchBrandingTemplate, branchLocationsRef } = useLocationDetails();

    const { pNavigate, hasUnsavedChangesRef } = useParspecNavigation()
    const location = useLocation();
    const currentTab = location.pathname.split('/').filter(Boolean).pop();

    const [isMultipleBranchesModalOpen, setIsMultipleBranchesModalOpen] = useState(false);
    const [snackbarData, setSnackbarData] = useState('');
    const [logoImageUrl, setLogoImageUrl] = useState('');
    const [pictureImageUrl, setPictureImageUrl] = useState('');

    useEffect(() => {
        const currentBranchLocations = branchLocationsRef.current;
        return () => {
            if (currentBranchLocations?.branchBrandingTemplate) {
                setBranchBrandingTemplate(currentBranchLocations.branchBrandingTemplate)
            }
        }
    }, [branchLocationsRef, setBranchBrandingTemplate])

    const { mutateAsync: editBranchBrandingData, isLoading } = useEditBranchBrandingMutation();

    const handleDeleteLogo = (logoType: 'logo' | 'picture') => {
        hasUnsavedChangesRef.current = true;
        if (logoType === 'logo') {
            setBranchBrandingTemplate({ ...branchBrandingTemplate, logo: { ...branchBrandingTemplate.logo, url: '', data: '', isChanged: true } });
        }
        if (logoType === 'picture') {
            setBranchBrandingTemplate({ ...branchBrandingTemplate, picture: { ...branchBrandingTemplate.picture, url: '', data: '', isChanged: true } });
        }
    };

    useEffect(() => {
        let logoUrl = '';
        let pictureUrl = '';
        let logoFileUrl = '';
        let pictureFileUrl = '';

        if (branchBrandingTemplate?.logo?.url) {
            if (branchBrandingTemplate.logo.data) {
                logoFileUrl = URL.createObjectURL(branchBrandingTemplate.logo.data);
                logoUrl = logoFileUrl
            } else {
                logoUrl = getProxyFileUrl(branchBrandingTemplate.logo.url);
            }
        }

        if (branchBrandingTemplate?.picture?.url) {
            if (branchBrandingTemplate.picture.data) {
                pictureFileUrl = URL.createObjectURL(branchBrandingTemplate.picture.data);
                pictureUrl = pictureFileUrl
            } else {
                pictureUrl = getProxyFileUrl(branchBrandingTemplate.picture.url);
            }
        }

        setLogoImageUrl(logoUrl);
        setPictureImageUrl(pictureUrl);

        // Cleanup function to revoke object URLs
        return () => {
            if (logoFileUrl) URL.revokeObjectURL(logoUrl);
            if (pictureFileUrl) URL.revokeObjectURL(pictureUrl);
        };
    }, [branchBrandingTemplate]);

    const resetDataHandler = () => {
        pNavigate(`/v2/settings/company-locations`);
    };

    const preparePayloadHandler = (locationArr: number[]) => {
        const payloadData = {
            apply_settings_to_company_ids: locationArr,
            payload: {
                logo: branchBrandingTemplate?.logo?.url,
                picture: branchBrandingTemplate?.picture?.url
            }

        }


        return payloadData
    }

    const onOptionSelection = async (type: string) => {
        switch (type) {
            case 'single':
                const payloadData = preparePayloadHandler([currentLocationId])
                try {
                    hasUnsavedChangesRef.current = false;
                    await editBranchBrandingData({ data: payloadData });
                    setSnackbarData(() => EDIT_SETTINGS_SUCCESS_MSG)
                } catch (err: any) {
                    setSnackbarData(() => err?.response?.data?.message ? err?.response?.data?.message : 'Something went wrong, please try again');
                }
                break;

            case 'multiple':
                setIsMultipleBranchesModalOpen(() => true);
                break;
        }
    };

    const onColumnSelection = async (selectedBranchIdsArr: number[]) => {

        const payloadData = preparePayloadHandler(selectedBranchIdsArr)

        try {
            hasUnsavedChangesRef.current = false;
            await editBranchBrandingData({ data: payloadData });
            setSnackbarData(() => EDIT_SETTINGS_SUCCESS_MSG)
        } catch (err: any) {
            setSnackbarData(() => err?.response?.data?.message ? err?.response?.data?.message : 'Something went wrong, please try again');
        }
        setIsMultipleBranchesModalOpen(() => false);
    };

    const onFileUploadedToS3 = (files: IFileType[], imgType: 'logo' | 'picture') => {
        if (files?.[0]) {
            hasUnsavedChangesRef.current = true;
            const { file, s3_file_path } = files[0];
            if (imgType === 'logo') {
                setBranchBrandingTemplate({ ...branchBrandingTemplate, logo: { ...branchBrandingTemplate.logo, url: s3_file_path ?? '', data: file as File, isChanged: true } });
            } else if (imgType === 'picture') {
                setBranchBrandingTemplate({ ...branchBrandingTemplate, picture: { ...branchBrandingTemplate.picture, url: s3_file_path ?? '', data: file as File, isChanged: true } });
            }

        }
    };

    const handleCloseMultipleBranchesModal = () => {
        setIsMultipleBranchesModalOpen(() => false);
    }

    return (
        <Box bgcolor="primary.contrastText" min-width={'1000px'}>
            <Box display="flex">
                <Box>
                    <H6>Branch Branding</H6>
                </Box>
                <Box ml="auto" display="flex">
                    <Box display="flex">
                        <Box mr={2}>
                            <Button variant="outlined" color="secondary" onClick={resetDataHandler}>
                                Cancel
                            </Button>
                        </Box>
                        <Box>
                            <SaveButton onOptionSelection={onOptionSelection} isLoading={isLoading} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box>
                <Box display="flex " height={'100%'} width={'100%'}>
                    <Box width="25%" minWidth={'250px'}>
                        {LOGO_TYPES.map((logoData) => {
                            return (
                                <Box mt={6} key={logoData.value}>
                                    <Box display="flex " position="relative" alignItems="center">
                                        <BodySmall color="text.secondary" fontWeight={500}>
                                            {logoData.label}
                                        </BodySmall>

                                        <Box>
                                            <Tooltip placement="top" title={logoData.value === 'logo' ? PRIMARY_LOGO_TOOLTIP_TEXT : SECONDARY_LOGO_TOOLTIP_TEXT}>
                                                <Box color={'text.secondary'}>
                                                    <InfoIcon color={'inherit'} sx={{ ml: 1 }} fontSize="small" />
                                                </Box>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    <Box>
                                        {!companyDetailsLoading ? (
                                            <Box mt={3} width="250px" height="120px">
                                                {!branchBrandingTemplate[logoData.value].url ? (
                                                    <FileSelector
                                                        borderColor="secondary"
                                                        placeholder="Drop a file or click to browse"
                                                        onUpload={(files: IFileType[]) => onFileUploadedToS3(files, logoData.value)}
                                                        onDeleteFile={() => handleDeleteLogo(logoData.value)}
                                                        acceptedFormats={ACCEPTED_FILE_TYPES}
                                                        url={`${environment.b}/api/generate_signed_url/`}
                                                    />
                                                ) : <>
                                                    <Grid
                                                        height={'100%'}
                                                        width={'100%'}
                                                        container
                                                        direction="column"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                        rowSpacing={1}
                                                        sx={{
                                                            border: '1px',
                                                            backgroundColor: 'neutral.light',
                                                            borderRadius: '10px',
                                                            position: 'relative'
                                                        }}
                                                    >
                                                        <IconButton
                                                            onClick={() => handleDeleteLogo(logoData.value)}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '0',
                                                                right: '0',
                                                                zIndex: 1,
                                                                backgroundColor: 'neutral.light'
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                        <Grid item p={2}>
                                                            <Grid container justifyContent="center" alignItems="center">
                                                                <Box mt={1} width={'100%'} height={72} color="secondary.main">
                                                                    {logoData.value === 'logo' ? (
                                                                        logoImageUrl ? (
                                                                            <img
                                                                                src={logoImageUrl}
                                                                                alt="Preview"
                                                                                style={{ width: '100%', height: '100%', objectFit: 'contain', verticalAlign: 'middle' }}
                                                                            />
                                                                        ) : (
                                                                            <Skeleton variant="rectangular" width="100%" height="100%" />
                                                                        )
                                                                    ) : (
                                                                        pictureImageUrl ? (
                                                                            <img
                                                                                src={pictureImageUrl}
                                                                                alt="Preview"
                                                                                style={{ width: '100%', height: '100%', objectFit: 'contain', verticalAlign: 'middle' }}
                                                                            />
                                                                        ) : (
                                                                            <Skeleton variant="rectangular" width="100%" height="100%" />
                                                                        )
                                                                    )}
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid></>
                                                }
                                            </Box>
                                        ) : (
                                            <Skeleton width={250} height={110} variant="rectangular" sx={{ borderRadius: 1 }} />
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                    <Box width="75%" ml={12} minWidth={'595px'}>
                        <Box marginTop="20px" bgcolor="neutral.light">
                            <Box p={4}>
                                <Box>
                                    <BodyBig fontWeight={500} color={'text.primary'}>
                                        Sample Documents Preview
                                    </BodyBig>
                                    <Box pt={4}>
                                        <BodyXS limit={false} color={'text.secondary'}>
                                            {SAMPLE_SUBMITTAL_PREVIEW_FIRST_PARA} <br />
                                            <br />
                                            {SAMPLE_SUBMITTAL_PREVIEW_SECOND_PARA}
                                        </BodyXS>
                                    </Box>
                                </Box>
                                <Box>
                                    <Box display="flex " position="relative" justifyContent={'space-evenly'} height={'100%'} mt={4}>
                                        <Box width="33%" ml={1} mr={1} display="flex" flexDirection="column" alignItems="center">
                                            <BodySmall color="text.secondary" fontWeight={500} mb={2}>
                                                Cover Page
                                            </BodySmall>
                                            <Box>
                                                <Box width={161} height={209} p={5} border={`1px solid`} borderColor={'neutral.main'} bgcolor={'primary.contrastText'}>
                                                    <Box width={'100%'} height={'100%'} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                                                        <Box width={'122px'} height={'10px'} bgcolor="neutral.main" marginTop={3}></Box>
                                                        <Box width={'78px'} height={'6px'} bgcolor="neutral.main" marginTop={1}></Box>

                                                        <Box width={'78px'} height={'6px'} bgcolor="neutral.main" marginTop={1}></Box>

                                                        <Box width={'100px'} height={'35px'} marginTop={4}>
                                                            {branchBrandingTemplate?.logo?.url && (
                                                                <img
                                                                    src={
                                                                        logoImageUrl

                                                                    }
                                                                    alt="Preview"
                                                                    style={{ width: '100%', height: '100%', objectFit: 'contain', verticalAlign: 'middle' }}
                                                                />
                                                            )}
                                                        </Box>

                                                        <Box width={'122px'} height={'6px'} bgcolor="neutral.main" marginTop={6}></Box>
                                                        <Box width={'122px'} height={'4px'} bgcolor="neutral.main" marginTop={1}></Box>
                                                        <Box width={'122px'} height={'6px'} bgcolor="neutral.main" marginTop={1}></Box>
                                                        <Box width={'122px'} height={'4px'} bgcolor="neutral.main" marginTop={1}></Box>
                                                        <Box width={'122px'} height={'6px'} bgcolor="neutral.main" marginTop={1}></Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box width="33%" ml={1} mr={1} display="flex" flexDirection="column" alignItems="center">
                                            <BodySmall color="text.secondary" fontWeight={500} mb={2}>
                                                Table of Contents
                                            </BodySmall>
                                            <Box>
                                                <Box width={161} height={209} p={5} border={`1px solid`} borderColor={'neutral.main'} bgcolor={'primary.contrastText'}>
                                                    <Box width={'100%'} height={'100%'} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                                                        <Box width={'100px'} height={'35px'} marginTop={4}>
                                                            {branchBrandingTemplate?.logo?.url && (
                                                                <img
                                                                    src={
                                                                        logoImageUrl
                                                                    }
                                                                    alt="Preview"
                                                                    style={{ width: '100%', height: '100%', objectFit: 'contain', verticalAlign: 'middle' }}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Box width={'122px'} height={'10px'} bgcolor="neutral.main" marginTop={3}></Box>

                                                        {[...Array(4)].map((_, index) => {
                                                            return (
                                                                <Box key={index}>
                                                                    <Box width={'122px'} height={'6px'} bgcolor="neutral.main" marginTop={1}></Box>
                                                                    <Box width={'122px'} height={'4px'} bgcolor="neutral.main" marginTop={1}></Box>
                                                                </Box>
                                                            );
                                                        })}

                                                        <Box width={'122px'} height={'6px'} bgcolor="neutral.main" marginTop={1}></Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box width="33%" ml={1} mr={1} display="flex" flexDirection="column" alignItems="center">
                                            <BodySmall color="text.secondary" fontWeight={500} mb={2}>
                                                Header & Footer
                                            </BodySmall>
                                            <Box>
                                                <Box width={161} height={209} p={5} border={`1px solid`} borderColor={'neutral.main'} bgcolor={'primary.contrastText'}>
                                                    <Box width={'100%'} height={'100%'} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                                                        <Box width={'100%'} height={32} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                                                            <Box mr={1}>
                                                                {branchBrandingTemplate?.picture?.url && (
                                                                    <img
                                                                        src={
                                                                            pictureImageUrl
                                                                        }
                                                                        alt="Preview"
                                                                        style={{ width: '20px', height: '20px', objectFit: 'contain', verticalAlign: 'middle' }}
                                                                    />
                                                                )}
                                                            </Box>

                                                            <Box width={'100px'} height={'18px'} mt={1} bgcolor={'neutral.main'} display={'flex'} flex={1}></Box>
                                                        </Box>
                                                        <Box width={'122px'} height={'10px'} bgcolor="ineutral.main" marginTop={3}></Box>

                                                        {[...Array(5)].map((_, index) => {
                                                            return (
                                                                <Box key={index}>
                                                                    <Box width={'122px'} height={'6px'} bgcolor="neutral.main" marginTop={1}></Box>
                                                                    <Box width={'122px'} height={'4px'} bgcolor="neutral.main" marginTop={1}></Box>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <MultipleBranchesModal
                isOpen={isMultipleBranchesModalOpen}
                onClose={handleCloseMultipleBranchesModal}
                currentTab={currentTab}
                onColumnSelection={onColumnSelection}
                isLoading={isLoading}

            />

            {<Snackbar open={snackbarData?.length > 0} message={snackbarData} onClose={() => setSnackbarData(() => '')} />}

        </Box>
    );
};

export default memo(BranchBranding);
